import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { HiOutlineCalendar, HiOutlineClock } from "react-icons/hi";
import { CustomButton } from "../../../atoms/CustomButton";
import { EmptyState } from "../../../atoms/EmptyState";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { Spinner } from "../../../atoms/Spinner";
import NoTranslate from "../../../atoms/NoTranslate";
import useAuth from "../../../../hooks/useAuth";
import {
  Appointment,
  useGetUserAppointments,
  useRefreshAtHoldExpiry,
} from "../../../../hooks/agenda";
import {
  AgendaAccess,
  useAgendaAccess,
} from "../../../../hooks/agenda/useAgendaAccess";
import AppointmentModal from "../agenda/AppointmentModal";
import MemberSettingsDrawer from "../agenda/MemberSettingsDrawer";
import AvailabilityLine from "./AvailabilityLine";
import { PaymentBadge, PaymentIssuesAlert } from "../agenda/AppointmentPayment";
import { paymentViewOf } from "../agenda/agendaPayment";
import { prefilledParticipants } from "../agenda/appointmentDraft";
import { AGENDA_PATH } from "../agenda/agendaTypes";
import {
  browserTimeZone,
  capitalizeFirst,
  dayLabel,
  otherZoneTime,
  participantNames,
  rangeLabel,
  endLabel,
} from "../agenda/agendaDates";

const PREVIEW_COUNT = 5;
const K = "agenda.clientSection";

interface AppointmentsSectionProps {
  customerIdentification: string;
  entityIdentification: string;
  userIdentification: string;
  personName?: string;
}

type ModalState =
  | { mode: "create" }
  | { mode: "edit"; appointment: Appointment }
  | null;

const AppointmentMeta: React.FC<{
  appointment: Appointment;
  personId: string;
}> = ({ appointment, personId }) => {
  const { t } = useTranslation();
  const { timezone, start, location } = appointment;
  const members = participantNames(appointment, personId);
  const otherZone = timezone && timezone !== browserTimeZone();

  return (
    <>
      {otherZone && (
        <p className="text-xs text-gray-500">
          {t(`${K}.otherTimeZone`, {
            time: otherZoneTime(start, timezone),
            timezone: timezone.replace(/_/g, " "),
          })}
        </p>
      )}
      {(location || members) && (
        <p className="break-words text-xs text-gray-500">
          {location && <NoTranslate>{location}</NoTranslate>}
          {location && members && " · "}
          {members && (
            <>
              {t(`${K}.with`)} <NoTranslate>{members}</NoTranslate>
            </>
          )}
        </p>
      )}
    </>
  );
};

const AppointmentList: React.FC<{
  label: string;
  appointments: Appointment[];
  personId: string;
  onOpen: (appointment: Appointment) => void;
}> = ({ label, appointments, personId, onOpen }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  if (appointments.length === 0) return null;
  const displayed = showAll
    ? appointments
    : appointments.slice(0, PREVIEW_COUNT);

  return (
    <div className="mt-4">
      <h3 className="text-xs font-medium text-gray-500">
        {label}
        <span className="ml-1.5 tabular-nums text-gray-400">
          {appointments.length}
        </span>
      </h3>
      <ul className="-mx-2 mt-1 divide-y divide-gray-100">
        {displayed.map((appointment) => (
          <li key={appointment.id}>
            <button
              type="button"
              onClick={() => onOpen(appointment)}
              className="w-full rounded-md px-2 py-2.5 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 break-words text-sm font-medium text-gray-900">
                <NoTranslate>{appointment.title}</NoTranslate>
                <PaymentBadge view={paymentViewOf(appointment)} />
              </p>
              <p className="text-xs tabular-nums text-gray-700">
                {moment(appointment.start).format("ll")} ·{" "}
                <span className="whitespace-nowrap">
                  {rangeLabel(
                    t,
                    "time",
                    moment(appointment.start).format("LT"),
                    endLabel(appointment)
                  )}
                </span>
              </p>
              <AppointmentMeta appointment={appointment} personId={personId} />
            </button>
          </li>
        ))}
      </ul>
      {appointments.length > PREVIEW_COUNT && (
        <div className="mt-2 flex justify-center">
          <CustomButton
            variant="secondary"
            size="sm"
            onClick={() => setShowAll(!showAll)}
            title={
              showAll
                ? t("dashboard.entities.viewEntityUser.viewLess")
                : t("dashboard.entities.viewEntityUser.viewMore")
            }
          />
        </div>
      )}
    </div>
  );
};

const AppointmentsCard: React.FC<
  AppointmentsSectionProps & {
    selfId: string;
    access: AgendaAccess;
  }
> = ({
  customerIdentification,
  entityIdentification,
  userIdentification,
  personName,
  selfId,
  access,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modal, setModal] = useState<ModalState>(null);
  const [settingHours, setSettingHours] = useState(false);
  const isSelf = userIdentification === selfId;
  const { canWrite, canConfigure, managesOthers, isOn: agendaOn } = access;
  const {
    data: config,
    isLoading: configLoading,
    isError: configError,
    refetch: refetchConfig,
  } = access.config;

  const {
    data,
    isLoading: listLoading,
    isError: listError,
    refetch: refetchList,
  } = useGetUserAppointments(
    customerIdentification,
    entityIdentification,
    userIdentification,
    { enabled: agendaOn }
  );

  useRefreshAtHoldExpiry(entityIdentification, data?.appointments ?? []);

  const { next, upcoming, past } = useMemo(() => {
    const now = Date.now();
    const list = data?.appointments ?? [];
    const future = list
      .filter((a) => a.end > now)
      .sort((a, b) => a.start - b.start);
    return {
      next: future[0] ?? null,
      upcoming: future.slice(1),
      past: list.filter((a) => a.end <= now).sort((a, b) => b.start - a.start),
    };
  }, [data]);

  const openCreate = () => {
    if (canWrite) setModal({ mode: "create" });
  };
  const openEdit = (appointment: Appointment) =>
    setModal({ mode: "edit", appointment });

  const hasAppointments = Boolean(next) || past.length > 0;
  const addButton = (variant: "outline" | "primary") => (
    <CustomButton
      variant={variant}
      size="sm"
      icon="plus"
      title={t(`${K}.add`) as string}
      onClick={openCreate}
    />
  );

  let body: React.ReactNode;
  if (configLoading || (agendaOn && listLoading)) {
    body = (
      <div className="flex justify-center py-6">
        <Spinner size="md" />
      </div>
    );
  } else if (configError || !config || listError) {
    body = (
      <EmptyState
        title={t("agenda.appointmentsError")}
        action={
          <CustomButton
            variant="secondary"
            size="sm"
            title={t("agenda.retry") as string}
            onClick={() => (listError ? refetchList() : refetchConfig())}
          />
        }
      />
    );
  } else if (!agendaOn) {
    body = (
      <EmptyState
        icon={<HiOutlineCalendar size={24} />}
        title={t(`${K}.disabledTitle`)}
        description={
          canConfigure
            ? t(`${K}.disabledOwnerDescription`)
            : t(`${K}.disabledMemberDescription`)
        }
        action={
          <CustomButton
            variant="secondary"
            size="sm"
            title={t(`${K}.openAgenda`) as string}
            onClick={() => navigate(AGENDA_PATH)}
          />
        }
      />
    );
  } else if (!hasAppointments) {
    body = (
      <EmptyState
        icon={<HiOutlineCalendar size={24} />}
        title={t(`${K}.emptyTitle`)}
        description={t(`${K}.emptyDescription`)}
        action={canWrite ? addButton("primary") : undefined}
      />
    );
  } else {
    const inProgress = next !== null && next.start <= Date.now();
    body = (
      <>
        {next && (
          <div className="mt-3">
            <h3 className="text-xs font-medium text-gray-500">
              {inProgress ? t(`${K}.current`) : t(`${K}.next`)}
            </h3>
            <div className="-mx-2 mt-1">
              <button
                type="button"
                onClick={() => openEdit(next)}
                className="flex w-full flex-wrap items-start gap-x-3 gap-y-1 rounded-md px-2 py-2.5 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <div className="min-w-0 flex-1 basis-48">
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-1 break-words text-base font-semibold text-gray-900">
                    <NoTranslate>{next.title}</NoTranslate>
                    <PaymentBadge view={paymentViewOf(next)} />
                  </p>
                  <p className="text-sm tabular-nums text-gray-700">
                    {dayLabel(moment(next.start))}{" "}
                    <span className="whitespace-nowrap">
                      {rangeLabel(
                        t,
                        "time",
                        moment(next.start).format("LT"),
                        endLabel(next)
                      )}
                    </span>
                  </p>
                  <AppointmentMeta
                    appointment={next}
                    personId={userIdentification}
                  />
                </div>
                <StatusBadge color={inProgress ? "success" : "gray"} dot>
                  {inProgress
                    ? t(`${K}.inProgress`)
                    : capitalizeFirst(moment(next.start).fromNow())}
                </StatusBadge>
              </button>
            </div>
          </div>
        )}
        <AppointmentList
          label={t(`${K}.upcoming`)}
          appointments={upcoming}
          personId={userIdentification}
          onOpen={openEdit}
        />
        <AppointmentList
          label={t(`${K}.past`)}
          appointments={past}
          personId={userIdentification}
          onOpen={openEdit}
        />
      </>
    );
  }

  return (
    <div className="min-w-0 self-stretch bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <div className="flex flex-col gap-3 border-b border-gray-100 pb-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="min-w-0 flex-1 sm:basis-64">
          <h2 className="text-sm font-semibold text-gray-900">
            {t(`${K}.title`)}
          </h2>
        </div>
        {canWrite && (
          <div className="flex flex-wrap items-center gap-2">
            <CustomButton
              variant="outline"
              size="sm"
              icon={<HiOutlineClock size={16} aria-hidden="true" />}
              title={t("agenda.availability.set") as string}
              onClick={() => setSettingHours(true)}
            />
            {agendaOn && hasAppointments && addButton("outline")}
          </div>
        )}
      </div>

      <AvailabilityLine
        customerIdentification={customerIdentification}
        entityIdentification={entityIdentification}
        userIdentification={userIdentification}
      />
      {agendaOn && (data?.payment_issues.length ?? 0) > 0 && (
        <div className="mt-3">
          <PaymentIssuesAlert
            issues={data?.payment_issues ?? []}
            markableFor={
              canWrite && (isSelf || managesOthers) ? userIdentification : null
            }
          />
        </div>
      )}

      {body}

      <MemberSettingsDrawer
        open={settingHours}
        onClose={() => setSettingHours(false)}
        userId={userIdentification}
        memberName={personName}
        canEdit={canWrite}
      />

      {agendaOn && (
        <AppointmentModal
          show={modal !== null}
          onClose={() => setModal(null)}
          appointment={modal?.mode === "edit" ? modal.appointment : null}
          detailed
          draft={{
            participantIds: prefilledParticipants(
              { id: selfId, managesOthers },
              [userIdentification]
            ),
          }}
          minParticipants={isSelf || managesOthers ? 1 : 2}
          openedFrom={userIdentification}
        />
      )}
    </div>
  );
};

const AppointmentsSection: React.FC<AppointmentsSectionProps> = (props) => {
  const { user } = useAuth();
  const access = useAgendaAccess(
    props.customerIdentification,
    props.entityIdentification
  );

  if (!access.canView) return null;

  return (
    <AppointmentsCard
      {...props}
      selfId={user.user_identification}
      access={access}
    />
  );
};

export default AppointmentsSection;
